import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { fetchStatus } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CheckCircle2, Loader2 } from "lucide-react";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sid = params.get("session_id");
  const [status, setStatus] = useState(null);
  const [tries, setTries] = useState(0);
  const { refresh } = useAuth();

  useEffect(() => {
    if (!sid) return;
    let cancelled = false;
    let timer;
    const retry = () => {
      if (tries < 15) timer = setTimeout(() => setTries((t) => t + 1), 2000);
    };
    (async () => {
      try {
        const s = await fetchStatus(sid);
        if (cancelled) return;
        setStatus(s);
        if (s.payment_status === "paid") await refresh();
        else retry();
      } catch (e) {
        console.error("Payment status check failed", e);
        if (cancelled) return;
        const code = e?.response?.status;
        if (code === 404 || code === 401) setTries(15);
        else retry();
      }
    })();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [sid, tries, refresh]);

  const paid = status?.payment_status === "paid";
  const gaveUp = !paid && tries >= 15;

  return (
    <div data-testid="payment-success-page" className="min-h-screen flex items-center justify-center bg-[#0b0e13] px-4">
      <div className="border border-[#334155] bg-[#11151c] p-10 max-w-md w-full text-center">
        {paid ? (
          <>
            <CheckCircle2 className="w-14 h-14 text-[#10b981] mx-auto mb-4" />
            <h1 className="heading text-2xl font-bold mb-2">Payment Confirmed</h1>
            <p className="text-sm text-[#94a3b8] mb-6 mono">
              Plan: <span className="text-[#f59e0b]">{status.lookup_key}</span>
            </p>
            <Link
              data-testid="continue-btn"
              to="/"
              className="inline-block bg-[#f59e0b] hover:bg-[#fbbf24] text-[#0b0e13] px-6 py-3 mono text-xs tracking-widest uppercase font-bold"
            >
              Enter RIGCALC
            </Link>
          </>
        ) : gaveUp ? (
          <div data-testid="payment-pending-timeout">
            <h1 className="heading text-2xl font-bold mb-2">Still processing</h1>
            <p className="text-sm text-[#94a3b8] mb-6">
              Your payment is taking longer than usual to confirm. If you were charged, your plan will activate automatically within a few minutes — refresh this page or contact support.
            </p>
            <Link data-testid="payment-pending-home" to="/" className="inline-block border border-[#334155] hover:border-[#f59e0b] px-6 py-3 mono text-xs tracking-widest uppercase">
              Back to RIGCALC
            </Link>
          </div>
        ) : (
          <>
            <Loader2 className="w-14 h-14 text-[#f59e0b] mx-auto mb-4 animate-spin" />
            <h1 className="heading text-2xl font-bold mb-2">Finalising Payment...</h1>
            <p className="text-sm text-[#94a3b8] mono">
              Session: {sid?.slice(0, 20)}...
              <br />
              Status: {status?.payment_status || "checking"}
            </p>
          </>
        )}
      </div>
    </div>
  );
}
